import './App.css';
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Home from './Home/Home';
import Contact from './Contact/Contact';
import About from './About/About';
import ServiceLanding from './ServiceLanding/ServiceLanding';
import Blog from './Blog/Blog';
import BlogDetail from './Blog/BlogDetail';
import Portfolio from './Portfolio/Portfolio';
import ProjectDetail from './Portfolio/ProjectDetail';
import Pricing from './Pricing/Pricing';
import NotFound from './NotFound/NotFound';
import Cursor from './components/Cursor';
import WhatsAppButton from './components/WhatsAppButton';
import { Toaster } from 'react-hot-toast';
import TrafficTracker from './components/TrafficTracker';

const PrivacyPolicy = React.lazy(() => import('./PrivacyPolicy/PrivacyPolicy'));
const TermsAndConditions = React.lazy(() => import('./TermsAndConditions/TermsAndConditions'));
const CookiesPolicy = React.lazy(() => import('./CookiesPolicy/CookiesPolicy'));
const RefundPolicy = React.lazy(() => import('./RefundPolicy/RefundPolicy'));
const Sitemap = React.lazy(() => import('./Sitemap/Sitemap'));
const Process = React.lazy(() => import('./Process/Process'));
const Brochure = React.lazy(() => import('./Brochure/Brochure'));

const ClientOnboarding = React.lazy(() => import('./ClientOnboarding/ClientOnboarding'));
const ClientLogin = React.lazy(() => import('./ClientLogin/ClientLogin'));
const ClientDashboard = React.lazy(() => import('./ClientDashboard/ClientDashboard'));
const PaymentResult = React.lazy(() => import('./PaymentResult/PaymentResult'));

const AdminLogin = React.lazy(() => import('./Admin/AdminLogin'));
const Dashboard = React.lazy(() => import('./Admin/Dashboard'));
const AdminServices = React.lazy(() => import('./Admin/AdminServices'));
const AdminSocials = React.lazy(() => import('./Admin/AdminSocials'));
const AdminInquiries = React.lazy(() => import('./Admin/AdminInquiries'));
const AdminPortfolio = React.lazy(() => import('./Admin/AdminPortfolio'));
const AdminAbout = React.lazy(() => import('./Admin/AdminAbout'));
const AdminContactPage = React.lazy(() => import('./Admin/AdminContact'));
const AdminPrivacy = React.lazy(() => import('./Admin/AdminPrivacy'));
const AdminTerms = React.lazy(() => import('./Admin/AdminTerms'));
const AdminCookies = React.lazy(() => import('./Admin/AdminCookies'));
const AdminInvoice = React.lazy(() => import('./Admin/AdminInvoice'));
const AdminBlog = React.lazy(() => import('./Admin/AdminBlog'));
const AdminBrochure = React.lazy(() => import('./Admin/AdminBrochure'));
const AdminRefundPolicy = React.lazy(() => import('./Admin/AdminRefundPolicy'));
const AdminClients = React.lazy(() => import('./Admin/AdminClients'));
const AdminPayments = React.lazy(() => import('./Admin/AdminPayments'));
const AdminTasks = React.lazy(() => import('./Admin/AdminTasks'));
const ProtectedRoute = React.lazy(() => import('./Admin/ProtectedRoute'));

/* Page transition variants */
const pageVariants = {
  initial: { opacity: 0, y: 18 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  exit: {
    opacity: 0,
    y: -12,
    transition: { duration: 0.22, ease: [0.55, 0, 1, 0.45] },
  },
};

/* All routes live here so useLocation() works inside <Router> */
function AnimatedRoutes() {
  const location = useLocation();

  if (location.pathname === '/client-onboarding') return <Suspense fallback={null}><ClientOnboarding /></Suspense>;
  if (location.pathname === '/client-login') return <Suspense fallback={null}><ClientLogin /></Suspense>;
  if (location.pathname.startsWith('/client-dashboard')) return <Suspense fallback={null}><ClientDashboard /></Suspense>;
  if (location.pathname === '/payment-result') return <Suspense fallback={null}><PaymentResult /></Suspense>;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{ minHeight: '100vh' }}
      >
        <Suspense fallback={null}>
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-conditions" element={<TermsAndConditions />} />
            <Route path="/cookie-policy" element={<CookiesPolicy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio/:id" element={<ProjectDetail />} />
            <Route path="/process" element={<Process />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/brochure" element={<Brochure />} />

            {/* Service Landing Pages — /:serviceId handles all service slugs */}
            <Route path="/:serviceId" element={<ServiceLanding />} />

            <Route path="/admin/login" element={<AdminLogin />} />

            {/* Protected Admin Routes */}
            <Route path="/admin/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/admin/services" element={<ProtectedRoute><AdminServices /></ProtectedRoute>} />
            <Route path="/admin/socials" element={<ProtectedRoute><AdminSocials /></ProtectedRoute>} />
            <Route path="/admin/inquiries" element={<ProtectedRoute><AdminInquiries /></ProtectedRoute>} />
            <Route path="/admin/portfolio" element={<ProtectedRoute><AdminPortfolio /></ProtectedRoute>} />
            <Route path="/admin/about" element={<ProtectedRoute><AdminAbout /></ProtectedRoute>} />
            <Route path="/admin/contact-page" element={<ProtectedRoute><AdminContactPage /></ProtectedRoute>} />
            <Route path="/admin/privacy" element={<ProtectedRoute><AdminPrivacy /></ProtectedRoute>} />
            <Route path="/admin/terms" element={<ProtectedRoute><AdminTerms /></ProtectedRoute>} />
            <Route path="/admin/cookies" element={<ProtectedRoute><AdminCookies /></ProtectedRoute>} />
            <Route path="/admin/invoice" element={<ProtectedRoute><AdminInvoice /></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
            <Route path="/admin/brochure" element={<ProtectedRoute><AdminBrochure /></ProtectedRoute>} />
            <Route path="/admin/refund-policy" element={<ProtectedRoute><AdminRefundPolicy /></ProtectedRoute>} />
            <Route path="/admin/clients" element={<ProtectedRoute><AdminClients /></ProtectedRoute>} />
            <Route path="/admin/payments" element={<ProtectedRoute><AdminPayments /></ProtectedRoute>} />
            <Route path="/admin/tasks" element={<ProtectedRoute><AdminTasks /></ProtectedRoute>} />

            {/* 404 */}
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <Toaster position="top-right" reverseOrder={false} />
        <TrafficTracker />
        <Cursor />
        <WhatsAppButton />
        <AnimatedRoutes />
      </div>
    </Router>
  );
}

export default App;
